import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  AimOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  type GeoPoint,
  type BoundaryEdge,
  type BoundaryAnalysis,
  calculateHaversineDistance,
  calculateBearing,
  calculateOutwardNormal,
  calculateGateArrow,
  analyzeBoundary,
} from "./boundaryMath";
import "./boundaryPicker.css";

export type BasemapMode = "satellite" | "hybrid" | "street";

export interface CemeteryBoundaryPickerMapProps {
  /**
   * Danh sách các toạ độ góc ranh giới P1..P4 (tối đa 4 điểm).
   */
  readonly corners: readonly GeoPoint[];
  /**
   * Callback khi danh sách toạ độ góc thay đổi (thêm điểm hoặc kéo thả).
   */
  readonly onCornersChange?: (corners: GeoPoint[]) => void;
  /**
   * Index cạnh mặt tiền đã chọn (0: P1-P2, 1: P2-P3, 2: P3-P4, 3: P4-P1), hoặc null nếu chưa chọn.
   */
  readonly frontageEdgeIndex?: number | null;
  /**
   * Callback khi người dùng click chọn cạnh mặt tiền.
   */
  readonly onSelectFrontageEdge?: (index: number) => void;
  /**
   * Bước trong quy trình:
   * 1: Cắm và kéo thả 4 điểm góc P1-P4
   * 2: Chọn cạnh mặt tiền & hướng cổng
   * 3: Xem trước / Đối soát mô hình (chỉ đọc)
   */
  readonly step?: 1 | 2 | 3;
  /**
   * Toạ độ tâm ban đầu [lat, lng], mặc định [15.12, 108.80] (Quảng Ngãi).
   */
  readonly initialCenter?: [number, number];
  /**
   * Zoom ban đầu (mặc định 18).
   */
  readonly initialZoom?: number;
  /**
   * Chiều cao container bản đồ (CSS string hoặc px, mặc định '480px').
   */
  readonly height?: string | number;
  /**
   * Chế độ chỉ đọc.
   */
  readonly readOnly?: boolean;
  /**
   * Loại nền bản đồ mặc định ('satellite' | 'hybrid' | 'street'). Mặc định 'satellite'.
   */
  readonly basemap?: BasemapMode;
  /**
   * Chiều dài mũi tên hướng cổng tính bằng mét (mặc định 20m).
   */
  readonly gateArrowLengthM?: number;
  /**
   * Callback thông báo kết quả phân tích ranh giới khi có đủ 4 điểm.
   */
  readonly onBoundaryAnalyzed?: (analysis: BoundaryAnalysis | null) => void;
}

const DEFAULT_CENTER: [number, number] = [15.1205, 108.8005];
const DEFAULT_ZOOM = 18;

export function CemeteryBoundaryPickerMap({
  corners,
  onCornersChange,
  frontageEdgeIndex = null,
  onSelectFrontageEdge,
  step = 1,
  initialCenter,
  initialZoom = DEFAULT_ZOOM,
  height = "480px",
  readOnly = false,
  basemap: initialBasemap = "satellite",
  gateArrowLengthM = 20,
  onBoundaryAnalyzed,
}: CemeteryBoundaryPickerMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const basemapLayersRef = useRef<Record<BasemapMode, L.TileLayer> | null>(null);

  // Layer groups quản lý các phần tử vẽ
  const polygonLayerRef = useRef<L.FeatureGroup | null>(null);
  const edgesLayerRef = useRef<L.FeatureGroup | null>(null);
  const markersLayerRef = useRef<L.FeatureGroup | null>(null);
  const arrowLayerRef = useRef<L.FeatureGroup | null>(null);

  const [basemap, setBasemap] = useState<BasemapMode>(initialBasemap);

  // Giữ các callback & state trong ref để tránh re-subscribe map listener khi re-render
  const onCornersChangeRef = useRef(onCornersChange);
  useEffect(() => {
    onCornersChangeRef.current = onCornersChange;
  }, [onCornersChange]);

  const onSelectFrontageEdgeRef = useRef(onSelectFrontageEdge);
  useEffect(() => {
    onSelectFrontageEdgeRef.current = onSelectFrontageEdge;
  }, [onSelectFrontageEdge]);

  const stepRef = useRef(step);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const readOnlyRef = useRef(readOnly);
  useEffect(() => {
    readOnlyRef.current = readOnly;
  }, [readOnly]);

  const cornersRef = useRef(corners);
  useEffect(() => {
    cornersRef.current = corners;
  }, [corners]);

  // Phân tích ranh giới khi đủ 4 điểm
  const boundaryAnalysis = useMemo<BoundaryAnalysis | null>(() => {
    if (corners.length < 4) return null;
    try {
      return analyzeBoundary(
        [...corners],
        frontageEdgeIndex !== null && frontageEdgeIndex !== undefined ? frontageEdgeIndex : 0
      );
    } catch {
      return null;
    }
  }, [corners, frontageEdgeIndex]);

  useEffect(() => {
    onBoundaryAnalyzed?.(boundaryAnalysis);
  }, [boundaryAnalysis, onBoundaryAnalyzed]);

  // Hàm fitBounds vừa khít các điểm góc
  const fitToCorners = useCallback(() => {
    const map = mapRef.current;
    if (!map || corners.length === 0) return;
    const latLngs = corners.map((c) => L.latLng(c.lat, c.lng));
    const bounds = L.latLngBounds(latLngs);
    if (bounds.isValid() && (map.getSize().x ?? 0) > 0) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 19 });
    }
  }, [corners]);

  // 1. Khởi tạo Leaflet Map (chạy 1 lần duy nhất)
  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    const center = initialCenter ?? (corners[0] ? [corners[0].lat, corners[0].lng] : DEFAULT_CENTER);
    const map = L.map(el, {
      center,
      zoom: initialZoom,
      zoomControl: false,
      attributionControl: true,
      minZoom: 3,
      maxZoom: 21,
    });
    mapRef.current = map;

    // Tile layers Google
    const googleTile = (lyrs: string): L.TileLayer =>
      L.tileLayer(`https://mt{s}.google.com/vt/lyrs=${lyrs}&x={x}&y={y}&z={z}`, {
        subdomains: "0123",
        maxZoom: 21,
        attribution: "&copy; Google Maps",
      });

    const tileLayers: Record<BasemapMode, L.TileLayer> = {
      satellite: googleTile("s"),
      hybrid: googleTile("y"),
      street: googleTile("m"),
    };
    basemapLayersRef.current = tileLayers;
    tileLayers[initialBasemap].addTo(map);

    // Tạo các FeatureGroups
    polygonLayerRef.current = L.featureGroup().addTo(map);
    edgesLayerRef.current = L.featureGroup().addTo(map);
    arrowLayerRef.current = L.featureGroup().addTo(map);
    markersLayerRef.current = L.featureGroup().addTo(map);

    // Controls
    L.control.scale({ imperial: false, position: "bottomleft" }).addTo(map);

    // Event click map để cắm điểm góc P1-P4
    map.on("click", (e: L.LeafletMouseEvent) => {
      if (readOnlyRef.current || stepRef.current !== 1) return;
      const current = cornersRef.current;
      if (current.length >= 4) return;

      const newPoint: GeoPoint = {
        lat: Number(e.latlng.lat.toFixed(7)),
        lng: Number(e.latlng.lng.toFixed(7)),
        lon: Number(e.latlng.lng.toFixed(7)),
      };
      const updated = [...current, newPoint];
      onCornersChangeRef.current?.(updated);
    });

    return () => {
      map.off();
      map.remove();
      mapRef.current = null;
      polygonLayerRef.current = null;
      edgesLayerRef.current = null;
      markersLayerRef.current = null;
      arrowLayerRef.current = null;
      basemapLayersRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Chuyển đổi Basemap tile layer
  useEffect(() => {
    const map = mapRef.current;
    const layers = basemapLayersRef.current;
    if (!map || !layers) return;

    (["satellite", "hybrid", "street"] as const).forEach((mode) => {
      const layer = layers[mode];
      if (mode === basemap) {
        if (!map.hasLayer(layer)) layer.addTo(map);
      } else if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
  }, [basemap]);

  // 3. Render Polygon ranh giới và Polyline
  useEffect(() => {
    const polygonLayer = polygonLayerRef.current;
    if (!polygonLayer) return;
    polygonLayer.clearLayers();

    if (corners.length >= 3) {
      const latLngs = corners.map((p) => [p.lat, p.lng] as [number, number]);
      L.polygon(latLngs, {
        color: "#1890ff",
        weight: 2.5,
        fillColor: "#1890ff",
        fillOpacity: 0.16,
        interactive: false,
      }).addTo(polygonLayer);
    } else if (corners.length === 2) {
      const latLngs = corners.map((p) => [p.lat, p.lng] as [number, number]);
      L.polyline(latLngs, {
        color: "#1890ff",
        weight: 2,
        dashArray: "6 4",
        interactive: false,
      }).addTo(polygonLayer);
    }
  }, [corners]);

  // 4. Render Markers 4 góc P1-P4 (draggable ở bước 1)
  useEffect(() => {
    const markersLayer = markersLayerRef.current;
    if (!markersLayer) return;
    markersLayer.clearLayers();

    const isDraggable = step === 1 && !readOnly;

    corners.forEach((pt, idx) => {
      const pinIndex = idx + 1;
      const icon = L.divIcon({
        className: "cemetery-corner-pin-container",
        html: `
          <div class="cemetery-corner-pin pin-p${pinIndex}" data-testid="corner-pin-p${pinIndex}">
            <span class="pin-badge">P${pinIndex}</span>
          </div>
        `,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });

      const marker = L.marker([pt.lat, pt.lng], {
        icon,
        draggable: isDraggable,
      });

      marker.bindTooltip(`Góc P${pinIndex}: [${pt.lat.toFixed(6)}, ${pt.lng.toFixed(6)}]`, {
        direction: "top",
        offset: [0, -12],
      });

      if (isDraggable) {
        marker.on("dragend", () => {
          const latlng = marker.getLatLng();
          const nextCorners = [...cornersRef.current];
          nextCorners[idx] = {
            lat: Number(latlng.lat.toFixed(7)),
            lng: Number(latlng.lng.toFixed(7)),
            lon: Number(latlng.lng.toFixed(7)),
          };
          onCornersChangeRef.current?.(nextCorners);
        });
      }

      marker.addTo(markersLayer);
    });
  }, [corners, step, readOnly]);

  // 5. Render 4 cạnh tương tác & Badges chọn cạnh mặt tiền
  useEffect(() => {
    const edgesLayer = edgesLayerRef.current;
    if (!edgesLayer) return;
    edgesLayer.clearLayers();

    if (corners.length < 4) return;

    for (let i = 0; i < 4; i++) {
      const fromPt = corners[i];
      const toPt = corners[(i + 1) % 4];
      if (!fromPt || !toPt) continue;
      const lengthM = calculateHaversineDistance(fromPt, toPt);
      const isSelected = frontageEdgeIndex === i;

      // Đường viền cạnh
      const edgeLine = L.polyline(
        [
          [fromPt.lat, fromPt.lng],
          [toPt.lat, toPt.lng],
        ],
        {
          color: isSelected ? "#fa8c16" : "#1890ff",
          weight: isSelected ? 5 : 3,
          opacity: isSelected ? 1 : 0.7,
          dashArray: isSelected ? undefined : "6 4",
          interactive: step === 2 && !readOnly,
        }
      );

      if (step === 2 && !readOnly) {
        edgeLine.on("click", () => {
          onSelectFrontageEdgeRef.current?.(i);
        });
      }
      edgeLine.addTo(edgesLayer);

      // Trung điểm cạnh
      const midLat = (fromPt.lat + toPt.lat) / 2;
      const midLng = (fromPt.lng + toPt.lng) / 2;

      const pFromLabel = `P${i + 1}`;
      const pToLabel = `P${((i + 1) % 4) + 1}`;

      const badgeIcon = L.divIcon({
        className: "edge-badge-container",
        html: `
          <div class="edge-midpoint-badge ${isSelected ? "is-selected" : ""}"
               data-testid="edge-badge-${i}"
               role="button"
               tabindex="0"
               aria-label="Cạnh ${i + 1}: ${pFromLabel}-${pToLabel}">
            <div class="edge-badge-header">
              ${isSelected ? "⭐ Mặt tiền: Cạnh " + (i + 1) : "Cạnh " + (i + 1) + " (" + pFromLabel + "-" + pToLabel + ")"}
            </div>
            <div class="edge-badge-length">
              ${lengthM.toFixed(1)}m
            </div>
          </div>
        `,
        iconSize: [120, 36],
        iconAnchor: [60, 18],
      });

      const badgeMarker = L.marker([midLat, midLng], {
        icon: badgeIcon,
        interactive: true,
      });

      badgeMarker.on("click", () => {
        if (step === 2 && !readOnlyRef.current) {
          onSelectFrontageEdgeRef.current?.(i);
        }
      });

      badgeMarker.addTo(edgesLayer);
    }
  }, [corners, frontageEdgeIndex, step, readOnly]);

  // 6. Render Mũi tên hướng cổng (Gate Arrow)
  useEffect(() => {
    const arrowLayer = arrowLayerRef.current;
    if (!arrowLayer) return;
    arrowLayer.clearLayers();

    if (
      corners.length < 4 ||
      frontageEdgeIndex === null ||
      frontageEdgeIndex === undefined ||
      frontageEdgeIndex < 0 ||
      frontageEdgeIndex >= 4
    ) {
      return;
    }

    try {
      const fromPt = corners[frontageEdgeIndex];
      const toPt = corners[(frontageEdgeIndex + 1) % 4];
      if (!fromPt || !toPt) return;
      const edge: BoundaryEdge = {
        index: frontageEdgeIndex,
        from: fromPt,
        to: toPt,
        lengthM: calculateHaversineDistance(fromPt, toPt),
        bearingDeg: calculateBearing(fromPt, toPt),
      };

      const outward = calculateOutwardNormal([...corners], frontageEdgeIndex);
      const gateArrow = calculateGateArrow(edge, outward.bearingDeg, gateArrowLengthM);

      // Trục mũi tên (thân)
      const arrowLine = L.polyline(
        [
          [gateArrow.start.lat, gateArrow.start.lng],
          [gateArrow.end.lat, gateArrow.end.lng],
        ],
        {
          color: "#ff4d4f",
          weight: 4,
          opacity: 1,
          interactive: false,
        }
      );
      arrowLine.addTo(arrowLayer);

      // Đầu mũi tên (SVG arrowhead xoay theo góc phương vị)
      const arrowIcon = L.divIcon({
        className: "cemetery-gate-arrow-container",
        html: `
          <div class="cemetery-gate-arrowhead" style="transform: rotate(${outward.bearingDeg}deg);">
            <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
              <polygon points="13,2 4,22 13,17 22,22" fill="#ff4d4f" stroke="#ffffff" stroke-width="2"/>
            </svg>
          </div>
          <div class="cemetery-gate-arrow-label">
            Cổng chính (${Math.round(outward.bearingDeg)}°)
          </div>
        `,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });

      L.marker([gateArrow.end.lat, gateArrow.end.lng], {
        icon: arrowIcon,
        interactive: false,
      }).addTo(arrowLayer);
    } catch (err) {
      // Bỏ qua khi toạ độ suy biến
      console.warn("Không thể vẽ mũi tên hướng cổng:", err);
    }
  }, [corners, frontageEdgeIndex, gateArrowLengthM]);

  // Render text hướng dẫn theo step
  const renderStepInstruction = () => {
    if (readOnly || step === 3) {
      return (
        <>
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
          <span>Xem trước định vị 4 góc ranh giới & hướng cổng</span>
        </>
      );
    }

    if (step === 1) {
      if (corners.length === 0) {
        return (
          <>
            <InfoCircleOutlined style={{ color: "#1890ff" }} />
            <span>Nhấp chuột trên bản đồ để cắm điểm góc P1</span>
          </>
        );
      }
      if (corners.length < 4) {
        return (
          <>
            <InfoCircleOutlined style={{ color: "#1890ff" }} />
            <span>
              Đã cắm {corners.length}/4 điểm. Nhấp chuột tiếp để cắm góc P{corners.length + 1}.
            </span>
          </>
        );
      }
      return (
        <>
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
          <span>Đã đủ 4 điểm góc P1-P4. Kéo thả marker để tinh chỉnh vị trí nếu cần.</span>
        </>
      );
    }

    if (step === 2) {
      if (frontageEdgeIndex !== null && frontageEdgeIndex !== undefined) {
        return (
          <>
            <CheckCircleOutlined style={{ color: "#faad14" }} />
            <span>
              Mặt tiền: Cạnh {frontageEdgeIndex + 1} (P{frontageEdgeIndex + 1}-P{((frontageEdgeIndex + 1) % 4) + 1}) — Hướng cổng:{" "}
              {boundaryAnalysis ? `${Math.round(boundaryAnalysis.outwardNormalBearingDeg)}°` : "Đang tính..."}
            </span>
          </>
        );
      }
      return (
        <>
          <InfoCircleOutlined style={{ color: "#faad14" }} />
          <span>Nhấp chuột vào 1 trong 4 cạnh để chọn CẠNH MẶT TIỀN (cổng chính nghĩa trang).</span>
        </>
      );
    }

    return null;
  };

  return (
    <div
      className="cemetery-boundary-picker-container"
      style={{ height }}
      role="region"
      aria-label="Bản đồ định vị 4 góc ranh giới nghĩa trang"
    >
      {/* HUD Hướng dẫn & Công cụ trên bản đồ */}
      <div className="boundary-picker-hud">
        <div className="boundary-picker-instruction">
          {renderStepInstruction()}
        </div>

        <div className="boundary-picker-hud-actions">
          {corners.length > 0 && (
            <button
              type="button"
              className="hud-btn"
              onClick={fitToCorners}
              title="Căn vừa 4 góc ranh giới"
            >
              <AimOutlined /> Căn vừa
            </button>
          )}

          {step === 1 && !readOnly && corners.length > 0 && (
            <button
              type="button"
              className="hud-btn"
              onClick={() => onCornersChangeRef.current?.([])}
              title="Xoá và cắm lại 4 điểm góc"
            >
              <ClearOutlined /> Cắm lại
            </button>
          )}
        </div>
      </div>

      {/* Viewport Leaflet */}
      <div ref={containerRef} className="cemetery-boundary-map-viewport" />

      {/* Thanh chuyển nền bản đồ (Vệ tinh / Hỗn hợp / Bản đồ số) */}
      <div className="boundary-picker-basemap-bar" role="group" aria-label="Loại bản đồ">
        <button
          type="button"
          className={`basemap-opt-btn ${basemap === "satellite" ? "active" : ""}`}
          onClick={() => setBasemap("satellite")}
          aria-pressed={basemap === "satellite"}
        >
          Vệ tinh
        </button>
        <button
          type="button"
          className={`basemap-opt-btn ${basemap === "hybrid" ? "active" : ""}`}
          onClick={() => setBasemap("hybrid")}
          aria-pressed={basemap === "hybrid"}
        >
          Hỗn hợp
        </button>
        <button
          type="button"
          className={`basemap-opt-btn ${basemap === "street" ? "active" : ""}`}
          onClick={() => setBasemap("street")}
          aria-pressed={basemap === "street"}
        >
          Bản đồ
        </button>
      </div>

      {/* Thẻ tóm tắt thông số đo đạc khi có đủ 4 điểm */}
      {boundaryAnalysis && (
        <div className="boundary-picker-summary-card">
          <div className="boundary-picker-summary-title">Thông số ranh giới</div>
          <div>Chu vi: ~{Math.round(boundaryAnalysis.perimeterM)}m</div>
          <div>Diện tích: ~{Math.round(boundaryAnalysis.areaM2).toLocaleString("vi-VN")}m²</div>
          <div>
            Kích thước: {Math.round(boundaryAnalysis.dimensions.widthM)}m ×{" "}
            {Math.round(boundaryAnalysis.dimensions.depthM)}m
          </div>
        </div>
      )}
    </div>
  );
}
